import { FC } from 'react';
import { Button } from '@dynamon-as/ui';
import { LinkUserConsentProps } from '../types';
import { useAccountLinking } from '../hooks';
import styles from './LinkUserConsent.module.css';

/**
 * Default Card Properties for extending classes
 * TODO: This looks like a candidate for a generic componen type
 */
interface DefaultCardProps {
    children: React.ReactNode;
    style?: React.CSSProperties;
    className?: string;
}
/**
 * Default Card component to avoid project-specific dependencies.
 * This provides a basic card styling that matches the original design,
 * but can be replaced with a project-specific Card component via props.
 */
const DefaultCard: FC<DefaultCardProps> = ({ children, style, className }) => (
    <div
        className={`${styles.card} ${className ?? ''}`}
        style={style}
    >
        {children}
    </div>
);

/**
 * Content strings for the consent page.
 * These could be moved to an i18n system in the future.
 */
const CONTENT = {
    TITLE: 'Samtykke kreves',
    EXPIRED_MESSAGE: 'Linken er utløpt.',
    DESCRIPTION: `Du er i ferd med å logge inn i Dynamon med Entra ID. Vi har samlet inn brukerinformasjonen din og ser at du allerede har en bruker hos oss. For å kunne fortsette, må du samtykke til at vi slår sammen kontoene dine til èn. Samtykker du ikke, sletter vi den nye brukeren din. Du vil fortsatt kunne logge inn med den eksisterende kontoen din.`,
    CONSENT_YES: 'Ja, jeg samtykker',
    CONSENT_NO: 'Nei, jeg samtykker ikke',
} as const;

/**
 * Account linking consent component.
 *
 * This component is used by Auth0's custom action for linking Entra ID
 * users with existing Auth0 users. The user must consent to account linking
 * to proceed with the login.
 *
 * The component reads two query parameters:
 * - `state`: A unique token sent back to Auth0 for authorization
 * - `expire`: Expiration timestamp in milliseconds
 *
 * If the user consents, they are redirected back to Auth0 with consent=true.
 * If they don't consent, they are redirected with consent=false.
 * If the link is expired, an error message is displayed.
 *
 * @param props - Component props for customization
 * @returns Consent form or expired link message
 *
 * @example
 * ```tsx
 * // Basic usage
 * <LinkUserConsent />
 *
 * // With custom Card component
 * <LinkUserConsent CardComponent={CustomCard} />
 *
 * // For testing
 * <LinkUserConsent
 *   getSearchParams={() => new URLSearchParams('?state=test&expire=9999999999')}
 *   getDomain={() => 'test.auth0.com'}
 * />
 * ```
 */
export const LinkUserConsent: FC<LinkUserConsentProps> = ({
    CardComponent = DefaultCard,
    className,
    getSearchParams,
    getDomain,
}) => {
    // Use custom hook to handle all consent logic
    const { isLoading, error, handleConsent } = useAccountLinking({
        getSearchParams,
        getDomain,
    });

    return (
        <div className={className}>
            <CardComponent
                style={{ width: '1400px', borderTopLeftRadius: '0px' }}
            >
                <h3 className={styles.title}>{CONTENT.TITLE}</h3>
                <div className={styles.content}>
                    {error ? (
                        <p>{CONTENT.EXPIRED_MESSAGE}</p>
                    ) : (
                        <>
                            <p className={styles.description}>
                                {CONTENT.DESCRIPTION}
                            </p>
                            <div className={styles.buttonGroup}>
                                <Button
                                    color="success"
                                    text={CONTENT.CONSENT_YES}
                                    className={styles.button}
                                    onClick={() => handleConsent(true)}
                                    disabled={isLoading}
                                />
                                <Button
                                    color="danger"
                                    text={CONTENT.CONSENT_NO}
                                    className={styles.button}
                                    onClick={() => handleConsent(false)}
                                    disabled={isLoading}
                                />
                            </div>
                        </>
                    )}
                </div>
            </CardComponent>
        </div>
    );
};

export default LinkUserConsent;
